import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private client: AxiosInstance;
  private refreshPromise: Promise<string> | null = null;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Load tokens from cookies on initialization
    this.accessToken = Cookies.get('access_token') || null;
    this.refreshToken = Cookies.get('refresh_token') || null;

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = this.getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Don't try to refresh token for login/register/public endpoints.
        // /auth/google/ matters as much as the rest: a 401 from the OAuth code
        // exchange means "expired code", not "stale session", and letting the
        // refresh flow handle it ends in clearTokens() plus a hard redirect to
        // /login — which pre-empts the callback page's own error handling and
        // reads to the user as a random logout.
        const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                               originalRequest.url?.includes('/auth/register') ||
                               originalRequest.url?.includes('/auth/refresh') ||
                               originalRequest.url?.includes('/auth/google/');

        // Don't redirect on 401 for endpoints that might be called from public pages
        const isPublicEndpoint = originalRequest.url?.includes('/organizations/') || // Public org lookup
                                 originalRequest.url === '/v1/organization'; // Might be called without auth

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          // If this is a public endpoint, don't try to refresh or redirect
          if (isPublicEndpoint) {
            return Promise.reject(error);
          }

          // During impersonation there is no refresh token (intentionally). A 401
          // means the short-lived impersonation token expired or was rejected:
          // never mint a wrong-identity token — drop back to the platform console.
          if (this.isImpersonating && !this.getRefreshToken()) {
            this.exitImpersonation();
            window.location.href = '/platform/users';
            return Promise.reject(error);
          }

          // Platform tokens are not refreshable and are not tenant sessions:
          // let the platform page handle the 401 (it sends the admin back to
          // /platform/login) instead of the tenant refresh + /login redirect.
          if (this.isPlatformSession) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const newToken = await this.refreshAccessToken();
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return this.client(originalRequest);
          } catch (refreshError) {
            this.clearTokens();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private getAccessToken(): string | null {
    // Try memory first, then cookies
    if (this.accessToken) {
      return this.accessToken;
    }
    const token = Cookies.get('access_token');
    if (token) {
      this.accessToken = token; // Cache in memory
    }
    return token || null;
  }

  private getRefreshToken(): string | null {
    // Try memory first, then cookies
    if (this.refreshToken) {
      return this.refreshToken;
    }
    const token = Cookies.get('refresh_token');
    if (token) {
      this.refreshToken = token; // Cache in memory
    }
    return token || null;
  }

  private setTokens(accessToken: string, refreshToken: string) {
    // Store tokens in cookies for persistence
    // Use SameSite=lax for CSRF protection
    const cookieOptions = {
      expires: 1, // 1 day for access token
      sameSite: 'lax' as const,
      secure: import.meta.env.PROD,
      path: '/' // Ensure cookie is available across all routes
    };

    Cookies.set('access_token', accessToken, cookieOptions);
    Cookies.set('refresh_token', refreshToken, {
      ...cookieOptions,
      expires: 7 // 7 days for refresh token
    });

    // Also store in memory for immediate use
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
  }

  private clearTokens() {
    // Clear from both cookies and memory
    Cookies.remove('access_token', { path: '/' });
    Cookies.remove('refresh_token', { path: '/' });
    this.accessToken = null;
    this.refreshToken = null;
  }

  private async refreshAccessToken(): Promise<string> {
    // Prevent multiple refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = new Promise(async (resolve, reject) => {
      try {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post(`${API_BASE_URL}/v1/auth/refresh`, {}, {
          headers: {
            'X-Refresh-Token': refreshToken,
          },
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;
        this.setTokens(accessToken, newRefreshToken);

        this.refreshPromise = null;
        resolve(accessToken);
      } catch (error) {
        this.refreshPromise = null;
        reject(error);
      }
    });

    return this.refreshPromise;
  }

  public setAuthTokens(accessToken: string, refreshToken: string) {
    this.setTokens(accessToken, refreshToken);
  }

  public clearAuth() {
    this.clearTokens();
  }

  // --- Platform super-admin session & impersonation -------------------------

  private platformCookieOptions() {
    return { sameSite: 'lax' as const, secure: import.meta.env.PROD, path: '/' };
  }

  // Establish a platform-admin session. The platform token is stored as the
  // active access_token (so the interceptor attaches it to /platform/* calls).
  // No refresh token — platform tokens are not refreshable.
  public setPlatformSession(token: string) {
    Cookies.set('access_token', token, { ...this.platformCookieOptions(), expires: 1 / 3 });
    Cookies.remove('refresh_token', { path: '/' });
    Cookies.remove('platform_access_token', { path: '/' });
    this.accessToken = token;
    this.refreshToken = null;
  }

  // Swap the active token to an impersonation token, stashing the platform token.
  // Removes the refresh token so the 401 interceptor can never mint a
  // wrong-identity token while impersonating.
  public enterImpersonation(impersonationToken: string) {
    const platform = Cookies.get('access_token');
    if (platform) {
      Cookies.set('platform_access_token', platform, this.platformCookieOptions());
    }
    Cookies.set('access_token', impersonationToken, {
      ...this.platformCookieOptions(),
      expires: 1 / 48, // ~30 min
    });
    Cookies.remove('refresh_token', { path: '/' });
    this.accessToken = impersonationToken;
    this.refreshToken = null;
  }

  // Restore the stashed platform token (or fully clear if none).
  public exitImpersonation() {
    const platform = Cookies.get('platform_access_token');
    Cookies.remove('platform_access_token', { path: '/' });
    if (platform) {
      Cookies.set('access_token', platform, { ...this.platformCookieOptions(), expires: 1 / 3 });
      this.accessToken = platform;
    } else {
      this.clearTokens();
    }
    this.refreshToken = null;
  }

  public get isImpersonating(): boolean {
    return !!Cookies.get('platform_access_token');
  }

  /**
   * True when the active token is a platform-admin token (type: "platform").
   * Read from the JWT payload without verifying it: this only picks the
   * client-side flow, the server still authenticates every request.
   */
  public get isPlatformSession(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    try {
      const payload = token.split('.')[1];
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json)?.type === 'platform';
    } catch {
      return false;
    }
  }

  public getPlatformToken(): string | null {
    return Cookies.get('platform_access_token') || null;
  }

  public get instance() {
    return this.client;
  }
}

export const apiClient = new ApiClient();
export default apiClient.instance;
